import { faVideo, faVideoSlash } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { cn } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components';
import { useTheme } from '../../hooks';
import { Theme } from '../../state';
import { toast } from '../../toasts';
import {
  LocalMediaStatus,
  localVideoContextAtom,
  useStartVideo,
  useStopVideo,
  useStoredAudioVideoSettings,
} from '../../video-sdk';
import { useStartVideoErrorToast } from './useStartVideoErrorToast';

export function VideoControl({
  isEnabled,
  loading,
  onToggle,
}: {
  isEnabled?: boolean;
  loading?: boolean;
  onToggle: (enable: boolean) => void;
}) {
  const t = useTranslate();
  const label = isEnabled ? t('telehealth-core:controls.toggleVideoOff') : t('telehealth-core:controls.toggleVideoOn');
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  return (
    <ControlPanelButtonTooltip childIsInteractive text={label}>
      <ControlPanelButton
        aria-label={label}
        loading={loading}
        className={cn({
          'text-coral-600': !isEnabled,
          'rebrand:text-text-inverse': !isEnabled && isDarkTheme,
          'rebrand:text-status-error-text': !isEnabled && !isDarkTheme,
        })}
        iconDefinition={isEnabled ? faVideo : faVideoSlash}
        data-testid='video-control'
        onClick={() => onToggle(!isEnabled)}
      />
    </ControlPanelButtonTooltip>
  );
}

export function VideoControlWrapper({ openHelpModal }: { openHelpModal?: () => void }) {
  const t = useTranslate();
  const showStartVideoErrorToast = useStartVideoErrorToast(openHelpModal);
  const { delegator: localVideoDelegator, status: localVideoStatus } = useAtomValue(localVideoContextAtom);
  const startVideo = useStartVideo();
  const stopVideo = useStopVideo();
  const isLoading = localVideoStatus === LocalMediaStatus.LOADING;
  const isEnabled = localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setStoredVideoControl = useCallback(
    (enabled: boolean) =>
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        isVideoEnabled: enabled,
      })),
    [setStoredAudioVideoSettings],
  );

  return (
    <VideoControl
      isEnabled={isEnabled}
      loading={isLoading}
      onToggle={async (enable) => {
        if (enable) {
          const [videoIsStarted, startVideoError] = await startVideo();
          if (videoIsStarted) {
            setStoredVideoControl(true);
            return;
          }
          showStartVideoErrorToast(startVideoError);
          return;
        }
        if (await stopVideo()) {
          setStoredVideoControl(false);
          return;
        }
        toast.error(t('telehealth-core:controls.anUnexpectedErrorOccurredWhileTryingToDisableYourCamera'));
      }}
    />
  );
}
